import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Shield,
  Eye,
  Lock,
  HandHeart,
  ClipboardX,
  MessageCircleOff,
  DoorClosed,
  Heart,
  PlayCircle,
  Sparkles,
  Search,
  Activity,
  MessageSquareHeart,
  Scale,
} from "lucide-react";
import SEO from "@/components/SEO";
import { usePageSeo } from "@/hooks/usePageSeo";
import CalendlyInlineBooking from "@/components/CalendlyInlineBooking";
import CareCreditBanner from "@/components/CareCreditBanner";
import { RelationshipGuideCompanion } from "@/components/RelationshipGuideCompanion";

const PAGE_KEY = "hyper-independence-specialty";

const AREA_LINKS = [
  { id: "miami", to: "/specialties/online-ketamine-therapy-women-miami", label: "Miami, Florida" },
  { id: "cumberland", to: "/specialties/ketamine-therapy-hyper-independent-women-cumberland-maine", label: "Cumberland, Maine" },
  { id: "portland", to: "/specialties/ketamine-therapy-hyper-independent-women-portland-maine", label: "Portland, Maine" },
  { id: "dc", to: "/specialties/ketamine-therapy-hyper-independent-women-washington-dc", label: "Washington, D.C." },
];

const whyHard = [
  {
    icon: Shield,
    title: "Your Self-Reliance Was Once a Survival Strategy",
    body: "At some point in your life — whether in childhood, past relationships, or during a period of betrayal — depending on others was unsafe or unreliable. Your brain learned that self-reliance equaled safety.",
  },
  {
    icon: Eye,
    title: "Hyper-Vigilance and Relationship Anxiety",
    body: "When you try to rely on your partner, your nervous system scans for red flags, anticipating disappointment, neglect, or loss of independence.",
  },
  {
    icon: Lock,
    title: "The Fear of Debt or Control",
    body: "Receiving support might trigger an unconscious worry that care comes with strings attached, or that accepting help means handing over your autonomy.",
  },
  {
    icon: HandHeart,
    title: "Vulnerability Feels Like Weakness",
    body: "Exposure feels terrifying when you have built your entire identity around being strong, capable, and unshakeable.",
  },
];

const signs = [
  {
    icon: ClipboardX,
    title: "The Refusal to Delegate",
    body: "You handle all financial, domestic, or emotional loads because explaining what you need feels harder than doing it yourself.",
  },
  {
    icon: MessageCircleOff,
    title: "Difficulty Expressing Needs",
    body: "When you are struggling or overwhelmed, you shut down or hide your distress from your partner.",
  },
  {
    icon: DoorClosed,
    title: "Chronic Emotional Distance",
    body: "You keep one foot out the door or maintain an \"escape plan,\" making it difficult to fully commit or relax in the relationship.",
  },
  {
    icon: Heart,
    title: "Guilt When Receiving Care",
    body: "Accepting gifts, acts of service, or emotional comfort leaves you feeling uncomfortable, anxious, or indebted.",
  },
  {
    icon: PlayCircle,
    title: "Sudden Urges to Pull Away",
    body: "When emotional intimacy deepens, your instinct is to withdraw, find flaws in your partner, or regain total control.",
  },
];

const therapySteps = [
  {
    icon: Search,
    title: "Unpack the Root Cause",
    body: "Identify the early experiences, relational betrayals, or attachment patterns that taught your nervous system that depending on others was unsafe.",
  },
  {
    icon: Activity,
    title: "Regulate Your Nervous System",
    body: "Utilize somatic approaches and evidence-based trauma modalities (such as EMDR, Internal Family Systems, and attachment therapy) to soothe the anxiety triggered by receiving support.",
  },
  {
    icon: MessageSquareHeart,
    title: "Practice Micro-Vulnerability",
    body: "Build practical tools to express your needs in low-stakes ways, allowing your partner to show up for you without triggering your alarm systems.",
  },
  {
    icon: Scale,
    title: "Maintain Healthy Autonomy",
    body: "Reframe support so you understand that trusting a partner does not require giving up your freedom, identity, or strength.",
  },
];

const faqs = [
  {
    q: "How do I know if I need therapy for hyper-independence or if my partner is just unreliable?",
    a: "Therapy helps clarify whether your difficulty trusting stems from your partner's behavior or an internal protective pattern. We explore real-time relationship dynamics alongside your personal trauma history to help you discern genuine red flags from anxiety-driven defense mechanisms.",
  },
  {
    q: "Can I heal hyper-independence while staying in my current relationship?",
    a: "Yes. In fact, an active relationship is often the best environment to practice new boundaries, vulnerability, and interdependence. As you learn to regulate your anxiety and communicate your needs, the relationship dynamic naturally shifts.",
  },
  {
    q: "What therapeutic modalities are used for relationship anxiety and trauma?",
    a: "We integrate evidence-based approaches tailored to relational trauma, including Eye Movement Desensitization and Reprocessing (EMDR), Internal Family Systems (IFS), Somatic Therapy, and Attachment-Focused Therapy. These approaches address both the cognitive beliefs and the body's physiological stress response.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const SpecialtyHyperIndependencePage = () => {
  const seoOverride = usePageSeo(PAGE_KEY);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="pt-20" data-testid="specialty-hyper-independence-page">
      <SEO
        metaTitle={seoOverride?.meta_title}
        keywords={[seoOverride?.focus_keyword, ...(seoOverride?.secondary_keywords || [])]}
        title={
          seoOverride?.meta_title ||
          "Therapy for Hyper-Independence — Relationship Anxiety & Trust Issues"
        }
        description={
          seoOverride?.meta_description ||
          "Specialized therapy for hyper-independent women facing relationship anxiety, trust issues, and relational trauma. Serving Brickell Miami FL, Cumberland & Portland ME, and Washington DC."
        }
        canonical="https://divinelovewellness.net/specialties/therapy-for-hyper-independence"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* === Hero === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp}>
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-5">
              Specialty · Therapy for Hyper-Independence
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground leading-tight">
              Healing Relational Hyper-Independence While Facing Relationship Anxiety &amp; Trust Issues⁠
            </h1>
            <h2 className="mt-4 font-serif text-xl sm:text-2xl lg:text-3xl italic font-medium text-primary leading-snug">
              Why Is It So Hard to Trust Your Partner, Let Down Your Guard, and Receive Support?
            </h2>

            <figure className="mt-8" data-testid="hyperindependence-hero-image">
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/536fj2j3_IMG_5914.jpeg"
                alt="Virtual therapy for relational hyper-independence and trust in FL, ME, & DC."
                loading="lazy"
                className="w-full rounded-2xl object-cover shadow-soft"
              />
              <figcaption className="mt-3 text-sm text-muted-foreground italic text-center">
                Virtual therapy for relational hyper-independence and trust in FL, ME, &amp; DC.
              </figcaption>
            </figure>

            <div className="mt-8 space-y-6 text-base md:text-lg text-foreground/80 leading-[1.85]">
              <p>
                Are you exhausted from carrying the emotional, logistical, and mental weight of your life entirely on your own — even when you are in a loving relationship?
              </p>
              <p>
                On the outside, you are competent, successful, and reliable. You are the woman everyone turns to when things need to get done. But inside your relationship, a quiet tension persists. When your partner offers help, your nervous system braces for impact. When you try to lean on them, a surge of anxiety tells you: <em>"If you want it done right — or if you want to be safe — you have to do it yourself."</em>
              </p>
              <div
                className="my-10 rounded-3xl border border-primary/15 bg-white/90 backdrop-blur shadow-soft px-6 md:px-10 py-8 md:py-10"
                data-testid="hyperindependence-areas-box"
              >
                <h2 className="font-serif text-xl sm:text-2xl md:text-3xl font-semibold text-foreground leading-snug text-center">
                  Offering Virtual Ketamine-Assisted Therapy in these spots:
                </h2>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-base md:text-lg">
                  {AREA_LINKS.map((area) => (
                    <li key={area.id}>
                      <Link
                        to={area.to}
                        className="inline-flex items-center gap-2 text-primary font-medium underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors"
                        data-testid={`hyperindependence-area-link-${area.id}`}
                      >
                        {area.label}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <p>
                Hyper-independence in relationships is not just a personality trait. It is a protective trauma response designed to keep you safe from disappointment, abandonment, or loss of control.
              </p>
              <p>
                Through specialized, trauma-informed therapy, you can learn to step out of survival mode, lower your guard, and build a relationship where you feel genuinely safe, supported, and secure.
              </p>
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  size="lg"
                  className="rounded-full px-8 py-6 bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="hyperindependence-hero-cta"
                >
                  Book Free 40-min Consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 py-6 border-primary/30 text-primary hover:bg-primary/5"
                >
                  Ask Laura a Question
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* === Why is it so hard — reasons === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              The Deep Reasons
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              Why Letting Down Your Guard{" "}
              <span className="italic text-primary">Feels So Difficult</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              If asking for help feels like opening yourself up to danger, you are not alone. Relational hyper-independence creates a deep internal friction: you want closeness and emotional intimacy, yet receiving care feels overwhelming or risky.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {whyHard.map((item) => (
              <Card
                key={item.title}
                className="p-6 md:p-7 bg-secondary/30 border-border/40 rounded-2xl"
                data-testid={`hyperindependence-reason-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
              >
                <div className="p-3 rounded-full bg-primary/10 inline-block">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-foreground mt-4">
                  {item.title}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground mt-2 leading-relaxed">
                  {item.body}
                </p>
              </Card>
            ))}
          </motion.div>
        </div>
      </section>

      {/* === Signs === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              Recognize The Patterns
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              Signs You Are Experiencing{" "}
              <span className="italic text-primary">Relational Hyper-Independence</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Hyper-independence often disguises itself as extreme competence, but underneath lies deep emotional fatigue and relational anxiety. You might be experiencing relational hyper-independence if you recognize these patterns:
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="space-y-4">
            {signs.map((sign) => (
              <div
                key={sign.title}
                className="flex items-start gap-4 md:gap-5 p-5 md:p-6 bg-white border border-border/40 rounded-2xl"
                data-testid={`hyperindependence-sign-${sign.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
              >
                <div className="p-3 rounded-full bg-accent/15 flex-shrink-0">
                  <sign.icon className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-semibold text-foreground">
                    {sign.title}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground mt-1 leading-relaxed">
                    {sign.body}
                  </p>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* === Definition + How Therapy Helps === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp}>
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              Definition
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              What Is{" "}
              <span className="italic text-primary">Relational Hyper-Independence?</span>
            </h2>
            <div className="mt-8 space-y-5 text-base md:text-lg text-foreground/80 leading-[1.85]">
              <p>
                <strong className="text-foreground">Relational Hyper-Independence</strong> is an adaptive coping mechanism — often rooted in developmental attachment trauma, betrayal trauma, or past relational neglect — wherein an individual habitually rejects support, suppresses emotional vulnerability, and maintains strict self-reliance within romantic relationships.
              </p>
              <p>
                While it serves as a defense against emotional hurt or disappointment, it frequently manifests as chronic relationship anxiety, difficulty establishing deep intimacy, and emotional exhaustion.
              </p>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="mt-16">
            <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground leading-tight">
              How Therapy Helps You{" "}
              <span className="italic text-primary">Soften Without Losing Yourself</span>
            </h3>
            <p className="mt-5 text-base md:text-lg text-muted-foreground leading-[1.85]">
              Healing hyper-independence does not mean becoming helpless or codependent. It means expanding your capacity for <strong className="text-foreground">interdependence</strong> — a healthy state where two self-sufficient adults can give and receive mutual care, trust, and emotional safety.
            </p>
            <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
              In our specialized therapy sessions, we work together to:
            </p>

            <ol className="mt-8 space-y-5">
              {therapySteps.map((step, idx) => (
                <li
                  key={step.title}
                  className="flex items-start gap-5 p-6 bg-accent/5 border border-accent/30 rounded-2xl"
                  data-testid={`hyperindependence-step-${idx + 1}`}
                >
                  <div className="flex items-center justify-center h-11 w-11 rounded-full bg-primary text-primary-foreground font-serif text-lg font-semibold flex-shrink-0">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-serif text-lg font-semibold text-foreground flex items-center gap-2">
                      <step.icon className="h-4 w-4 text-primary" />
                      {step.title}
                    </h4>
                    <p className="text-sm md:text-base text-muted-foreground mt-1 leading-relaxed">
                      {step.body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </motion.div>
        </div>
      </section>

      {/* === Community Guide — relationship anxiety gift === */}
      <RelationshipGuideCompanion background="bg-accent/10" />

      {/* === FAQ === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              Frequently Asked Questions
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              Therapy for{" "}
              <span className="italic text-primary">Hyper-Independent Women</span>
            </h2>
          </motion.div>

          <motion.div {...fadeUp} className="space-y-5">
            {faqs.map((faq, idx) => (
              <Card
                key={idx}
                className="p-6 md:p-7 bg-white border-border/40 rounded-2xl"
                data-testid={`hyperindependence-faq-${idx + 1}`}
              >
                <h3 className="font-serif text-lg md:text-xl font-semibold text-foreground">
                  {faq.q}
                </h3>
                <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                  {faq.a}
                </p>
              </Card>
            ))}
          </motion.div>
        </div>
      </section>

      {/* === Closing CTA + Calendly === */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <Sparkles className="h-8 w-8 text-primary mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              Ready to Stop{" "}
              <span className="italic text-primary">Carrying Everything Alone?</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              You don&apos;t have to choose between your independence and a safe, deeply connected relationship. You can remain strong and capable while learning how to rest in the care of a trusted partner.
            </p>
            <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              Take the first step toward lowering your emotional load and building secure love today.
            </p>
          </motion.div>
        </div>
      </section>

      {/* === CareCredit Financing Banner === */}
      <CareCreditBanner />

      {/* === Inline Calendly Booking === */}
      <CalendlyInlineBooking background="bg-secondary/30" />
    </div>
  );
};

export default SpecialtyHyperIndependencePage;
